import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getRandomId } from "../../SelfServiceCampaignCreation/Previews/utils";
import { ROLES } from "./Chatbot.constant";
import Actions from "../../../Actions";
import logger from "../../../utils/LoggerFrontend";

/**
 * @typedef {Object} Campaign
 * @property {string} jobTitle - The job title associated with the campaign
 * @property {string} companyName - The company name
 * @property {string} jobPostLink - The job posting URL
 * @property {string[]} location - The list of locations related to the job
 * @property {string[]} keywords - Keywords associated with the job
 */

/**
 * @typedef {Object} AssistantMessagePayload
 * @property {string} message - The message that the client will receive
 * @property {string} status - The current status of the task
 * @property {boolean} dataCollected - Represents if the data is collected
 * @property {Campaign} campaign - Initial data to create a campaign
 */

export const ChatbotContext = createContext();

export const ChatbotContextProvider = ({ children }) => {
  const [search, setSearch] = useState("");
  const [loadingAssistant, setLoadingAssistant] = useState(false);
  const [loadingChatResponse, setLoadingChatResponse] = useState(false);
  const [chatbotStarted, setChatbotSearched] = useState(false);
  const [messages, setMessages] = useState([]);
  const [uploadedAssets, setUploadedAssets] = useState([]);

  const handleRemoveAsset = useCallback((imgToRemove) => {
    setUploadedAssets((prevImages) => prevImages.filter((img) => img !== imgToRemove));
  }, []);

  const emptySearch = useCallback(() => setSearch(""), []);

  const handleLoadingChatResponse = useCallback((val) => setLoadingChatResponse(val), []);

  const handleLoadingAssistant = useCallback((val) => setLoadingAssistant(val), []);

  const handleSearchFocus = useCallback((event) => event.target.select(), []);

  const updateSearchText = useCallback((event) => setSearch(event.target.value), []);

  const askChatbot = useCallback(
    ({ message, seeDrafts, mediaUploaded }) => {
      const userMessage = {
        id: getRandomId(),
        createdDate: Date.now(),
        sender: ROLES.USER,
        message
      };
      const newMessages = [...messages, userMessage];
      setMessages(newMessages);

      const mappedMessages = newMessages.map(({ sender, message, campaign }) => {
        return {
          role: sender,
          content: message,
          campaign: campaign || null
        };
      });

      Actions.generateAssistantMessage({ messages: mappedMessages, seeDrafts, mediaUploaded });

      handleLoadingAssistant(false);
      handleLoadingChatResponse(true);
      emptySearch();

      /**
       * Handles the completion event of generating an assistant message.
       *
       * @param {Object} params - The parameters object
       * @param {AssistantMessagePayload} params.payload - The response payload from the assistant
       */
      const searchRef = Actions.generateAssistantMessage.completed.listen(({ payload }) => {
        logger.log("Chatbot:askChatbot", "generate assistant message: ", payload);

        const botMessage = {
          id: getRandomId(),
          createdDate: Date.now(),
          sender: ROLES.ASSISTANT,
          ...payload
        };

        setMessages((prevMessages) => [...prevMessages, botMessage]);
        handleLoadingChatResponse(false);
        searchRef();
      });

      const searchRefFailed = Actions.generateAssistantMessage.failed.listen((error) => {
        logger.log("Chatbot:askChatbot", "generate assistant message failed: ", error);

        const botMessage = {
          id: getRandomId(),
          createdDate: Date.now(),
          sender: ROLES.ASSISTANT,
          message: "The chatbot encountered an error. Please try again."
        };

        setMessages((prevMessages) => [...prevMessages, botMessage]);
        handleLoadingChatResponse(false);
        searchRefFailed();
      });
    },
    [messages, handleLoadingChatResponse, handleLoadingAssistant, emptySearch]
  );

  const handleNewAssetUpload = useCallback((newAsset) => {
    setUploadedAssets((prevImages) => [...prevImages, newAsset]);
  }, []);

  const handleStartAssistant = useCallback(() => {
    if (search.trim() === "") return;

    if (!chatbotStarted) setChatbotSearched(true);
    askChatbot({ message: search });
  }, [chatbotStarted, search, askChatbot]);

  const chatbotState = useMemo(
    () => ({
      search,
      messages,
      loadingAssistant,
      loadingChatResponse,
      chatbotStarted,
      uploadedAssets
    }),
    [search, messages, loadingAssistant, loadingChatResponse, chatbotStarted, uploadedAssets]
  );

  const chatbotActions = useMemo(
    () => ({
      emptySearch,
      askChatbot,
      handleStartAssistant,
      handleLoadingAssistant,
      handleSearchFocus,
      updateSearchText,
      handleRemoveAsset,
      handleNewAssetUpload
    }),
    [
      emptySearch,
      askChatbot,
      handleStartAssistant,
      handleLoadingAssistant,
      handleSearchFocus,
      updateSearchText,
      handleRemoveAsset,
      handleNewAssetUpload
    ]
  );

  return (
    <ChatbotContext.Provider value={{ ...chatbotState, ...chatbotActions }}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbotContext = () => useContext(ChatbotContext);
